import { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderStatus, Task } from '@epasu/core';
import { ORDER_STATUS_LABELS, ORDER_STATUSES } from '@epasu/core/constants';
import { Plus, X } from 'lucide-react';
import { crm, type OrderRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';
import { formatEuro } from '@/lib/utils';

interface Props {
  /** null = neuer Auftrag */
  order: OrderRow | null;
  /** Vorbelegter Kontakt bei einem neuen Auftrag — z. B. aus der Projektakte. */
  initialContactId?: string;
  onClose: () => void;
  onSaved: (order: OrderRow) => void;
}

interface ItemRow {
  description: string;
  quantity: string;
  unitPrice: string;
}

const leereZeile = (): ItemRow => ({ description: '', quantity: '1', unitPrice: '' });

/**
 * Auftrag anlegen und bearbeiten, samt Positionen und Checkliste.
 *
 * Der Wert ergibt sich aus den Positionen und wird nicht getippt — sonst
 * laufen Summe und Positionen früher oder später auseinander.
 */
export function OrderDialog({ order, initialContactId, onClose, onSaved }: Props) {
  const [title, setTitle] = useState(order?.title ?? '');
  const [contactId, setContactId] = useState(order?.contactId ?? initialContactId ?? '');
  const [dueDate, setDueDate] = useState(order?.dueDate?.slice(0, 10) ?? '');
  const [notes, setNotes] = useState(order?.notes ?? '');
  const [status, setStatus] = useState<OrderStatus>(
    (order?.status as OrderStatus) ?? 'offen',
  );
  const [items, setItems] = useState<ItemRow[]>(() =>
    order && order.items.length
      ? order.items.map((i) => ({
          description: i.description,
          quantity: String(i.quantity),
          unitPrice: (i.unitPriceCents / 100).toFixed(2).replace('.', ','),
        }))
      : [leereZeile()],
  );
  const [neueAufgabe, setNeueAufgabe] = useState('');
  const [checkliste, setCheckliste] = useState<string[]>([]);
  const [aufgaben, setAufgaben] = useState<Task[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const ladeAufgaben = useCallback(async () => {
    if (!order) return;
    setAufgaben(await crm.tasks.list({ orderId: order.id, includeDone: true }));
  }, [order]);

  useEffect(() => {
    titleRef.current?.focus();
    void ladeAufgaben();
  }, [ladeAufgaben]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const summe = items.reduce((s, i) => {
    const menge = parseZahl(i.quantity) ?? 0;
    const preis = parseEuro(i.unitPrice) ?? 0;
    return s + Math.round(menge * preis);
  }, 0);

  const positionen = () =>
    items
      .filter((i) => i.description.trim())
      .map((i) => ({
        description: i.description.trim(),
        quantity: parseZahl(i.quantity) ?? 1,
        unitPriceCents: parseEuro(i.unitPrice) ?? 0,
      }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Bitte einen Titel angeben.');
      return;
    }
    if (items.some((i) => i.description.trim() && parseEuro(i.unitPrice) === null)) {
      setError('Ein Einzelpreis ist keine gültige Zahl.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      let gespeichert: OrderRow;
      if (order) {
        await crm.orders.update(order.id, {
          title: title.trim(),
          dueDate: dueDate || null,
          notes: notes || null,
          contactId: contactId || null,
        });
        await crm.orders.replaceItems(order.id, positionen());
        if (status !== order.status) {
          await crm.orders.setStatus(order.id, status);
        }
        // Der Aufrufer braucht praktisch nur die ID (z. B. zum Verknüpfen) —
        // die einzeln aktualisierten Felder erneut zusammenzuführen lohnt
        // den zusätzlichen `get`-Aufruf nicht.
        gespeichert = order;
      } else {
        // Die Firma kommt vom Kontakt, damit der Auftrag auch dann noch
        // zuzuordnen ist, wenn die Person später gelöscht wird.
        const gewaehlterKontakt = contactId ? await crm.contacts.get(contactId) : null;
        gespeichert = await crm.orders.create({
          title: title.trim(),
          contactId: contactId || null,
          organizationId: gewaehlterKontakt?.organizationId ?? null,
          status,
          dueDate: dueDate || null,
          notes: notes || null,
          items: positionen(),
          taskTemplates: checkliste,
        });
      }
      onSaved(gespeichert);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={order ? 'Auftrag bearbeiten' : 'Neuer Auftrag'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-8 w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">
            {order ? `Auftrag ${order.number}` : 'Neuer Auftrag'}
          </h2>
          {order?.invoiceNumber && (
            <span className="text-xs text-muted-foreground">
              Rechnung {order.invoiceNumber}
            </span>
          )}
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Titel" className="sm:col-span-2">
            <Input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ausweise Schuljahr 2026/27"
            />
          </Field>

          <Field label="Kunde">
            <EntityPicker
              kind="contact"
              value={contactId || null}
              onChange={(id) => setContactId(id ?? '')}
              placeholder="— keiner —"
            />
          </Field>

          <Field label="Status">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as OrderStatus)}
            >
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ORDER_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Liefertermin">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Field>
        </div>

        <fieldset className="mt-5 rounded-md border p-4">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            Leistungen
          </legend>

          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  className="flex-1"
                  placeholder="Bezeichnung"
                  value={item.description}
                  onChange={(e) =>
                    setItems((rows) =>
                      rows.map((r, i) =>
                        i === index ? { ...r, description: e.target.value } : r,
                      ),
                    )
                  }
                />
                <Input
                  className="w-20"
                  inputMode="decimal"
                  placeholder="Menge"
                  value={item.quantity}
                  onChange={(e) =>
                    setItems((rows) =>
                      rows.map((r, i) =>
                        i === index ? { ...r, quantity: e.target.value } : r,
                      ),
                    )
                  }
                />
                <Input
                  className="w-28"
                  inputMode="decimal"
                  placeholder="Preis €"
                  value={item.unitPrice}
                  onChange={(e) =>
                    setItems((rows) =>
                      rows.map((r, i) =>
                        i === index ? { ...r, unitPrice: e.target.value } : r,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  aria-label="Position entfernen"
                  className="px-1 text-muted-foreground hover:text-destructive"
                  onClick={() =>
                    setItems((rows) =>
                      rows.length === 1 ? [leereZeile()] : rows.filter((_, i) => i !== index),
                    )
                  }
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setItems((rows) => [...rows, leereZeile()])}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Position
            </Button>
            <span className="text-sm font-medium">Summe {formatEuro(summe)}</span>
          </div>
        </fieldset>

        <fieldset className="mt-4 rounded-md border p-4">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            Aufgaben
          </legend>

          {order ? (
            aufgaben.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Keine Aufgaben zu diesem Auftrag.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {aufgaben.map((t) => (
                  <li key={t.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={t.status === 'erledigt'}
                      aria-label={`${t.title} erledigen`}
                      onChange={async () => {
                        await crm.tasks.setStatus(
                          t.id,
                          t.status === 'erledigt' ? 'offen' : 'erledigt',
                        );
                        await ladeAufgaben();
                      }}
                    />
                    <span
                      className={
                        t.status === 'erledigt' ? 'text-muted-foreground line-through' : ''
                      }
                    >
                      {t.title}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <>
              {checkliste.length > 0 && (
                <ul className="mb-2 space-y-1.5">
                  {checkliste.map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{t}</span>
                      <button
                        type="button"
                        aria-label={`${t} entfernen`}
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          setCheckliste((l) => l.filter((_, index) => index !== i))
                        }
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Ausweise produzieren"
                  value={neueAufgabe}
                  onChange={(e) => setNeueAufgabe(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter fügt die Aufgabe hinzu, statt das Formular
                    // abzuschicken — sonst entsteht der Auftrag beim ersten
                    // Eintrag.
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (!neueAufgabe.trim()) return;
                    setCheckliste((l) => [...l, neueAufgabe.trim()]);
                    setNeueAufgabe('');
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (!neueAufgabe.trim()) return;
                    setCheckliste((l) => [...l, neueAufgabe.trim()]);
                    setNeueAufgabe('');
                  }}
                >
                  Hinzufügen
                </Button>
              </div>
            </>
          )}
        </fieldset>

        <Field label="Notizen" className="mt-4">
          <textarea
            className="min-h-[4rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Euro-Eingabe in Cent. Akzeptiert Komma, Punkt und Tausenderpunkte. */
function parseEuro(input: string): number | null {
  const t = input.trim();
  if (!t) return 0;
  const n = Number(t.replace(/\s/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function parseZahl(input: string): number | null {
  const n = Number(input.trim().replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
